import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { decode } from "../../common/lib/qr";
import QRCodePhone from "../../common/icons/QRCodePhone";
import CancelButton from "../../components/CancelButton";
import { pushNotification } from "../../redux/actions/app";
import { enqueueAccessRequest, openGate } from "../../redux/actions/property";
import { playSound } from "../../common/lib/sounds";
import { selectValidUserCodes } from "../../redux/selectors/property";
import {
  STRINGS,
  NORMALIZED_BUTTONS,
  NormalizedButtonEvent,
} from "../../common/normalized-hardware-models";
import firebase from "../../common/firebase";
import { RootState, History, TypedDispatch } from "src";
import { createNotification, Sound } from "src/common/types";

type ComponentProps = {
  validAccessCodes: string[];
  dispatch: TypedDispatch;
  history: History;
};

type ComponentState = { videoLoaded: boolean };

class ScanQR extends Component<ComponentProps, ComponentState> {
  static propTypes = {
    history: PropTypes.any.isRequired,
  };

  video: React.RefObject<any>;
  introSound: any;
  hardTimeout: number;
  scanTimer: number;

  constructor(props: ComponentProps) {
    super(props);

    this.video = React.createRef();

    this.state = {
      videoLoaded: false,
    };

    this.hardTimeout = 0;
    this.scanTimer = 0;
  }

  componentDidMount() {
    const { validAccessCodes, dispatch, history } = this.props;

    window.addEventListener("normalizedButtonEvent", this.handleButton);

    this.introSound = playSound(Sound.Qr);

    this.hardTimeout = window.setTimeout(() => {
      history.replace("/");
    }, 60000);

    const hdConstraints = {
      video: { width: { exact: 240 } },
      audio: false,
    };

    navigator.mediaDevices.getUserMedia(hdConstraints).then((stream) => {
      if (this.video.current) {
        this.video.current.srcObject = stream;
        this.video.current.play();

        this.video.current.addEventListener("loadeddata", () => {
          if (this.video.current) {
            this.setState({ videoLoaded: true });
            const cameraCanvas = document.createElement("canvas");
            cameraCanvas.height = this.video.current.videoHeight;
            cameraCanvas.width = this.video.current.videoWidth;
            const ctx = cameraCanvas.getContext("2d");

            if (!ctx) return;

            const onframe = async () => {
              if (this.video.current && this.video.current.videoWidth > 0) {
                ctx.drawImage(
                  this.video.current,
                  0,
                  0,
                  this.video.current.videoWidth,
                  this.video.current.videoHeight
                );

                try {
                  const bc = await decode(ctx);
                  if (bc) {
                    try {
                      console.log(`QR : Have full code ${bc.rawValue}`);

                      const accessCodeValid = validAccessCodes.some(
                        (code: string) => code === bc.rawValue
                      );

                      if (accessCodeValid) {
                        firebase.logAnalyticsEvent("qr_code_gate_unlock");
                        dispatch(openGate(bc.rawValue));
                        dispatch(enqueueAccessRequest(bc.rawValue, true));
                      } else {
                        dispatch(
                          pushNotification(
                            createNotification(
                              "ERROR",
                              "Sorry, that code didn't work",
                              null,
                              "sorryCode"
                            )
                          )
                        );
                        dispatch(enqueueAccessRequest(bc.rawValue, false));
                      }

                      history.replace("/");
                    } catch (exp) {
                      console.log("QR : Invalid code detected");
                    }
                  }
                } catch (err) {
                  console.log("QR : Error decoding QR", err);
                }

                this.scanTimer = window.setTimeout(() => {
                  requestAnimationFrame(onframe);
                }, 100);
              }
            };

            requestAnimationFrame(onframe);
          }
        });
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener("normalizedButtonEvent", this.handleButton);

    this.introSound.stop();

    if (this.video.current && this.video.current.srcObject) {
      this.video.current.srcObject.getTracks()[0].stop();
    }
    clearTimeout(this.scanTimer);
    clearTimeout(this.hardTimeout);
  }

  handleButton = (e: NormalizedButtonEvent) => {
    const { history } = this.props;

    if (e.detail.button === NORMALIZED_BUTTONS.BACK) {
      history.replace("/");
    }
  };

  render() {
    const { history } = this.props;
    const { videoLoaded } = this.state;

    return (
      <div className="wrapper">
        <h1 className="page-title">{STRINGS.SCAN_QR_TITLE}</h1>
        <div className="row QRCodePhone-row">
          <div className="QRCode-instructions-wrapper">
            <div className="QRCodePhone">
              <QRCodePhone />
            </div>
            <p className="QRCodePhone-instructions">
              {STRINGS.SCAN_QR_INSTRUCTIONS}
            </p>
          </div>
        </div>
        <div className="row">
          <video
            style={videoLoaded ? {} : { display: "none" }}
            ref={this.video}
            muted
          />
          {!videoLoaded && "Loading..."}
        </div>
        <div className="footer-actions">
          <CancelButton onClick={() => history.replace("/")} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  validAccessCodes: selectValidUserCodes(state),
});

export default connect(mapStateToProps)(ScanQR);
